import { Cell } from '@table-library/react-table-library/table';
import numeral from 'numeral';
import Sparkline from '../../components/Sparklines';
import DomainImage from '../../components/DomainImage';

export const COLUMNS = (domains, isDark, t) => [
    {
        label: t('global.domain'),
        key: 'domain',
        data: 'domain',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, item, csvDownload }) => {
            const domain = domains.find((item) => item.url === data);
            if (csvDownload) return domain?.name ?? data;
            const href =
                item.dataType === 'search'
                    ? `/domains/${domain?.id}/gsc-reports/`
                    : `/domains/${domain?.id}/`;

            return (
                <>
                    <strong className="d-flex align-items-center gap-4">
                        <DomainImage domain_name={domain?.name} className="domain_img" />
                        <a href={href} rel="noreferrer">
                            {domain?.name}
                        </a>
                    </strong>
                    <div style={{ paddingLeft: '26px' }}>{item.domain}</div>
                </>
            );
        },
    },
    {
        label: t('global.data_type'),
        key: 'dataType',
        data: 'dataType',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;

            return (
                <div>
                    <strong>{data}</strong>
                </div>
            );
        },
    },
    {
        label: t('global.clicks'),
        key: 'clicks',
        data: 'clicks',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;

            return (
                <div>
                    <strong>{numeral(data).format('0.0a')}</strong>
                </div>
            );
        },
    },
    {
        label: t('global.trend'),
        key: 'clicks_sparkline',
        data: 'clicks_sparkline',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ item, csvDownload }) => {
            if (csvDownload) return null;
            const dontChangeLabel = item.dataType === 'search';
            // const labels = dontChangeLabel ? item.weeksL : item.days?.map((x) => x.value);
            const labels = item.daysL?.map((x) => x.value);
            const titleType = !dontChangeLabel ? (date) => `DATE: ${date}` : null;
            return (
                <Sparkline
                    dataL={item.clicksL}
                    keyName="Clicks"
                    labels={labels}
                    dontChangeLabel={dontChangeLabel}
                    titleType={titleType}
                />
            );
        },
    },
    {
        label: t('global.impressions'),
        key: 'impressions',
        data: 'impressions',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;

            return (
                <div>
                    <strong>{numeral(data).format('0.0a')}</strong>
                </div>
            );
        },
    },
    {
        label: t('global.ctr'),
        key: 'avgCTR',
        data: 'avgCTR',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ item, csvDownload }) => {
            const ctrValue = item.clicks / item.impressions;
            if (csvDownload) return ctrValue;

            return (
                <div>
                    <strong>{numeral(ctrValue).format('0.00%')}</strong>{' '}
                </div>
            );
        },
    },
    {
        label: t('global.unique_pages'),
        key: 'uniquePages',
        data: 'uniquePages',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, csvDownload }) => {
            if (!data) return null;
            if (csvDownload) return data;

            return (
                <div>
                    <strong>{numeral(data).format('0.0a')}</strong>
                </div>
            );
        },
    },
    {
        label: t('global.position'),
        key: 'avgPosition',
        data: 'avgPosition',
        Component: Cell,
        style: (item) => {
            const searchColor = isDark ? '#1e2a35' : '#ededed';
            const discoverColor = isDark ? '#2a3142' : 'white';
            const color = item.dataType === 'search' ? searchColor : discoverColor;
            return { backgroundColor: color };
        },
        renderCell: ({ data, csvDownload }) => {
            if (!data) return null;
            if (csvDownload) return data;

            return (
                <div>
                    <strong>{numeral(data).format('0.0a')}</strong>
                </div>
            );
        },
    },
];

export const EMPTY_DATA_KEYS = (domains, permission_errors, t) => [
    {
        label: t('global.domain'),
        key: 'domain',
        data: 'domain',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            const domainId = domains.find((item) => item.url === data)?.id;

            return (
                <strong>
                    <DomainImage domain_name={data} className="domain_img" />
                    <a
                        href={`/domains/${domainId}/`}
                        className="d-inline-flex align-items-cente"
                        rel="noreferrer"
                    >
                        {data}
                    </a>
                </strong>
            );
        },
    },
    {
        label: t('global.status'),
        key: 'status',
        data: 'status',
        Component: Cell,
        renderCell: ({ item }) => {
            return (
                <div className="wrap-text">
                    {permission_errors?.find((state) => state.domain === item.domain)?.message ? (
                        <a href="/settings">
                            {
                                permission_errors?.find((state) => state.domain === item.domain)
                                    ?.message
                            }
                        </a>
                    ) : (
                        'No Data Yet, contact info@gddash.com if no data for 72 hours'
                    )}
                </div>
            );
        },
    },
];
